// BLUEPRINT READ-BACK CARD (Howard authorized 2026-08-06).
// DISPLAY-ONLY: renders `result.readback` built server-side from the run's
// raw extraction. Recomputes nothing, writes nothing — visibility flags so
// geometry misses (gable-blind planes, phantom porches, averaged corners)
// are obvious at a glance instead of buried in the material list.
import React from "react";
import { Link, useParams } from "react-router-dom";
import { AlertTriangle, CheckCircle2, XCircle, Info } from "lucide-react";
import { useT } from "@/lib/i18n";
import VisualAuditPanel from "@/components/estimate/VisualAuditPanel";

const num = (v) => (v == null ? "—" : Math.round(v * 10) / 10);

const Flag = ({ level, children, testid }) => {
  const cls =
    level === "loud"
      ? "bg-[#FEF2F2] text-[#B91C1C] border-[#FCA5A5]"
      : level === "warn"
      ? "bg-[#FFFBEB] text-[#92400E] border-[#FCD34D]"
      : "bg-[var(--surface)] text-[var(--muted)] border-[var(--border)]";
  const Icon = level === "loud" ? XCircle : level === "warn" ? AlertTriangle : Info;
  return (
    <div data-testid={testid} className={`flex items-start gap-1.5 border px-2 py-1 text-[11px] leading-snug ${cls}`}>
      <Icon className="w-3.5 h-3.5 mt-[1px] shrink-0" />
      <span>{children}</span>
    </div>
  );
};

export default function BlueprintReadBackCard({ readback, pagePaths = [] }) {
  const t = useT();
  const { id: estIdFromRoute } = useParams();
  if (!readback) return null;
  const { planes, no_planes, plane_totals, garage_banner, corners, wing_check, porch, rail, gutter_runs, gutter_runs_total, source, consistency, stability, evidence, run, gable_attribution } = readback;
  const mismatches = stability
    ? [
        ...(stability.counts || []).filter((c) => !c.match),
        ...(stability.dims || []).filter((d) => !d.within),
        ...(stability.evidenced || []).filter((e) => !e.within),
      ]
    : [];

  return (
    <div data-testid="bp-readback-card" className="border border-[var(--border)] mt-3">
      <div className="px-3 py-2 border-b border-[var(--border)] flex items-center justify-between">
        <span className="text-[11px] font-bold uppercase tracking-wider">{t("bp.rb.title")}</span>
        <span className="flex items-center gap-3">
          {/* RUN IDENTITY (ruled 2026-08-09): walks reference estimate
              number + run timestamp — the id lives on the card itself. */}
          {run?.id && (
            <span className="text-[10px] font-mono-num text-[var(--muted)]" data-testid="bp-rb-run-identity">
              {t("bp.rb.run")} {String(run.id).slice(0, 8)}
              {run.at ? ` · ${String(run.at).slice(0, 16).replace("T", " ")} UTC` : ""}
            </span>
          )}
          {/* EXPIRY ON THE CARD (ruled 2026-08-11, TTL incident #3): a
              disappearing run must say so FIRST — the exact reap time,
              or the archived guarantee. */}
          {run?.archived ? (
            <span className="text-[10px] text-[var(--muted)]" data-testid="bp-rb-run-archived">
              {t("bp.rb.archived")}
            </span>
          ) : run?.reaped_at ? (
            <span className="text-[10px] text-[#B91C1C]" data-testid="bp-rb-run-reap">
              {t("bp.rb.reap", { when: `${String(run.reaped_at).slice(0, 16).replace("T", " ")} UTC` })}
            </span>
          ) : null}
          <span className="text-[10px] text-[var(--muted)]">{t("bp.rb.readonly")}</span>
        </span>
      </div>

      {/* PHASE 1 (ruled 2026-08-10): synthetic elevations rendered from
          the blueprint model — entry links; positions schematic. */}
      {estIdFromRoute && (
        <div className="px-3 py-1.5 border-b border-[var(--border)] flex items-center gap-3 flex-wrap" data-testid="bp-rb-elevation-links">
          <span className="text-[10px] font-bold uppercase tracking-wider text-[var(--muted)]">{t("bp.rb.sheets")}</span>
          {["front", "left", "back", "right"].map((s) => (
            <Link key={s} to={`/estimate/${estIdFromRoute}/blueprint-elevation/${s}`}
              className="text-[10px] underline" data-testid={`bp-rb-sheet-${s}`}>{s}</Link>
          ))}
          <span className="text-[9px] text-[var(--muted)]">{t("bp.rb.sheets.note")}</span>
        </div>
      )}

      {/* 0 — source (retention ruling 2026-08-07: the original upload is kept;
          the card says whether numbers stand on printed text or vision alone) */}
      {source && (
        <div className="px-3 py-2 border-b border-[var(--border)] space-y-1" data-testid="bp-rb-source">
          <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--muted)]">{t("bp.rb.source")}</div>
          {source.kind === "native_text" && (
            <Flag level="info" testid="bp-rb-source-native">{t("bp.rb.source.native", { n: source.text_pages, m: source.page_count })}</Flag>
          )}
          {source.kind === "mixed" && (
            <Flag level="warn" testid="bp-rb-source-mixed">{t("bp.rb.source.mixed", { n: source.text_pages, m: source.page_count })}</Flag>
          )}
          {source.kind === "scan" && (
            <Flag level="warn" testid="bp-rb-source-scan">{t("bp.rb.source.scan")}</Flag>
          )}
          {source.kind === "image_scans" && (
            <Flag level="warn" testid="bp-rb-source-images">{t("bp.rb.source.images")}</Flag>
          )}
        </div>
      )}

      {/* 0.5 — determinism gate (ruled 2026-08-08): STABILITY, never
          correctness. "Two reads agreed" and "matches the printed
          dimension" must never print as the same chip. */}
      {stability && (
        <div className="px-3 py-2 border-b border-[var(--border)] space-y-1" data-testid="bp-rb-stability">
          <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--muted)]">{t("bp.rb.stability")}</div>
          {stability.stable ? (
            <Flag level="info" testid="bp-rb-stability-agreed">{t("bp.rb.stability.agreed")}</Flag>
          ) : (
            mismatches.map((m, i) => (
              <Flag key={i} level="loud" testid={`bp-rb-stability-mismatch-${i}`}>
                {t("bp.rb.stability.mismatch", {
                  name: m.name,
                  a: String(m.a ?? "—"),
                  b: String(m.b ?? "—"),
                })}
              </Flag>
            ))
          )}
          {stability.abstained?.length > 0 && (
            <Flag level="warn" testid="bp-rb-stability-abstained">
              {t("bp.rb.stability.abstained", { text: stability.abstained.join(", ") })}
            </Flag>
          )}
          {stability.evidenced_not_comparable && (
            <Flag level="info" testid="bp-rb-stability-not-comparable">
              {t("bp.rb.stability.notComparable")}
            </Flag>
          )}
        </div>
      )}

      {/* 1 — roof-plane census */}
      <div className="px-3 py-2 space-y-1.5">
        <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--muted)]">{t("bp.rb.planes")}</div>
        {garage_banner && (
          <Flag level="loud" testid="bp-rb-no-garage-banner">{t("bp.rb.noGarage")}</Flag>
        )}
        {no_planes ? (
          <Flag level="loud" testid="bp-rb-no-planes">{t("bp.rb.noPlanes")}</Flag>
        ) : (
          <table className="w-full text-[11px] font-mono-num" data-testid="bp-rb-plane-table">
            <thead>
              <tr className="text-left text-[10px] uppercase text-[var(--muted)]">
                <th className="pr-2 font-semibold">{t("bp.rb.plane")}</th>
                <th className="pr-2 font-semibold text-right">{t("bp.rb.eave")}</th>
                <th className="pr-2 font-semibold text-right">{t("bp.rb.rake")}</th>
                <th className="pr-2 font-semibold text-right">{t("bp.rb.gableEnds")}</th>
                <th className="pr-2 font-semibold text-right">{t("bp.rb.porchSqft")}</th>
              </tr>
            </thead>
            <tbody>
              {planes.map((p, i) => (
                <React.Fragment key={i}>
                  <tr data-testid={`bp-rb-plane-${p.label}`}>
                    <td className="pr-2">{p.label}{p.is_porch ? " ⌂" : ""}</td>
                    <td className="pr-2 text-right">{num(p.eave_lf)}</td>
                    <td className={`pr-2 text-right ${p.gable_blind ? "text-[#B91C1C] font-bold" : ""}`}>{num(p.rake_lf)}</td>
                    <td className="pr-2 text-right">{p.gable_ends}</td>
                    <td className="pr-2 text-right">{p.is_porch ? num(p.porch_ceiling_sqft) : "—"}</td>
                  </tr>
                  {p.gable_blind && (
                    <tr>
                      <td colSpan={5} className="pb-1">
                        <Flag level="loud" testid={`bp-rb-gable-blind-${p.label}`}>{t("bp.rb.gableBlind", { plane: p.label })}</Flag>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {plane_totals && (
                <tr className="border-t border-[var(--border)] font-bold" data-testid="bp-rb-plane-totals">
                  <td className="pr-2">{t("bp.rb.totals")}</td>
                  <td className="pr-2 text-right">{num(plane_totals.eaves_lf)}</td>
                  <td className="pr-2 text-right">{num(plane_totals.rakes_lf)}</td>
                  <td className="pr-2 text-right">{plane_totals.gable_ends}</td>
                  <td className="pr-2 text-right">—</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
        {plane_totals && Number(plane_totals.gable_ends) > 0 && (
          <div className="text-[10px] font-bold text-[var(--warning-text)]" data-testid="bp-rb-gable-basis">
            gable measured — ½ × width × rise from this face's own width and rise, no field factor (SEND-137: the 0.70 field factor is retired; an untraced gable has no area and refuses by name)
          </div>
        )}
        {gable_attribution && gable_attribution.attributions && gable_attribution.attributions.length > 0 && (
          <div className="space-y-1" data-testid="bp-rb-gable-attribution">
            <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--muted)]">
              Gable attribution
            </div>
            <div className="text-[11px] font-mono-num">
              {gable_attribution.plane_gables_total} plane gable ends ·{" "}
              {gable_attribution.wall_gables_primary} primary ·{" "}
              {gable_attribution.wall_gables_attributed - gable_attribution.wall_gables_primary} secondary attributed
              {gable_attribution.census_reconciled ? " · reconciled" : " · UNRECONCILED"}
            </div>
            {gable_attribution.attributions.map((a, i) => (
              <div key={i} className="text-[11px] text-[var(--ink-2)]" data-testid={`bp-rb-gable-attrib-${a.wall}-${i}`}>
                <span className="font-bold uppercase">{a.wall}</span> — carries {a.count} secondary gable from plane <span className="font-mono-num">{a.plane}</span>
              </div>
            ))}
            {gable_attribution.reason && (
              <div className="text-[11px] text-[#B91C1C]" data-testid="bp-rb-gable-attrib-reason">
                {gable_attribution.reason}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 2 — corner ledger */}
      <div className="px-3 py-2 space-y-1.5 border-t border-[var(--border)]">
        <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--muted)]">{t("bp.rb.corners")}</div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] font-mono-num" data-testid="bp-rb-corner-ledger">
          <span>{t("bp.rb.outside")}: <b>{corners.outside}</b> ({num(corners.outside_lf)} LF)</span>
          <span>{t("bp.rb.inside")}: <b>{corners.inside}</b> ({num(corners.inside_lf)} LF)</span>
          {corners.invariant_ok != null && (
            <span data-testid="bp-rb-invariant" className={`inline-flex items-center gap-1 font-bold ${corners.invariant_ok ? "text-[#15803D]" : "text-[#B91C1C]"}`}>
              {corners.invariant_ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
              {t("bp.rb.invariant")}: {corners.invariant_ok ? t("bp.rb.pass") : t("bp.rb.fail")}
            </span>
          )}
        </div>
        {corners.basis === "averaged" && (
          <Flag level="warn" testid="bp-rb-basis-averaged">{t("bp.rb.basis.averaged")}</Flag>
        )}
        {corners.basis === "per_corner" && (
          <Flag level="info" testid="bp-rb-basis-percorner">{t("bp.rb.basis.per_corner")}</Flag>
        )}
        {corners.basis === "missing" && (
          <Flag level="warn" testid="bp-rb-basis-missing">{t("bp.rb.basis.missing")}</Flag>
        )}
        {corners.heights_ft && (
          <>
            <div className="text-[11px] font-mono-num" data-testid="bp-rb-corner-heights">
              {t("bp.rb.cornerHeights")}:{" "}
              {corners.heights_ft.map((h) => (h ? `${num(h)}'` : "?")).join(", ")}
            </div>
            <Flag level="info" testid="bp-rb-heights-read">{t("bp.rb.heights.read")}</Flag>
            {corners.undimensioned > 0 && (
              <Flag level="warn" testid="bp-rb-heights-undim">
                {t("bp.rb.heights.undim", { n: corners.undimensioned })}
              </Flag>
            )}
          </>
        )}
        {wing_check?.flag && (
          <Flag level="loud" testid="bp-rb-wing-flag">
            {t("bp.rb.wing", { fp: num(wing_check.footprint_area_sqft), rect: num(wing_check.rectangle_area_sqft) })}
          </Flag>
        )}
      </div>

      {/* 3 — porch tag */}
      <div className="px-3 py-2 space-y-1.5 border-t border-[var(--border)]">
        <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--muted)]">{t("bp.rb.porch")}</div>
        {porch.status === "plane_read" && (
          <Flag level="info" testid="bp-rb-porch-read">{t("bp.rb.porch.plane_read", { sqft: num(porch.ceiling_sqft) })}</Flag>
        )}
        {porch.status === "plane_without_ceiling" && (
          <Flag level="warn" testid="bp-rb-porch-noceiling">{t("bp.rb.porch.plane_without_ceiling")}</Flag>
        )}
        {porch.status === "phantom_ceiling" && (
          <Flag level="loud" testid="bp-rb-porch-phantom">{t("bp.rb.porch.phantom_ceiling", { sqft: num(porch.ceiling_sqft) })}</Flag>
        )}
        {porch.status === "absent" && (
          <Flag level="warn" testid="bp-rb-porch-absent">{t("bp.rb.porch.absent")}</Flag>
        )}
      </div>

      {/* 3.5 — gutter run inventory */}
      <div className="px-3 py-2 space-y-1.5 border-t border-[var(--border)]" data-testid="bp-rb-gutter-runs-section">
        <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--muted)]">{t("bp.rb.gutterRuns")}</div>
        {gutter_runs ? (
          <>
            <div className="text-[11px] font-mono-num" data-testid="bp-rb-gutter-run-list">
              {gutter_runs.map((r) => `${r.label} ${num(r.lf)}'`).join(" + ")} = <b>{num(gutter_runs_total)} LF</b>
            </div>
            <Flag level="info" testid="bp-rb-gutter-governs">{t("bp.rb.gutterRuns.governs")}</Flag>
          </>
        ) : (
          <Flag level="warn" testid="bp-rb-gutter-runs-none">{t("bp.rb.gutterRuns.none")}</Flag>
        )}
      </div>

      {/* 3.7 — internal consistency check (ruled 2026-08-07: the card
          arrives already clean — contradictions are named HERE, before
          anyone grades them) */}
      {Array.isArray(consistency) && (
        <div className="px-3 py-2 space-y-1 border-t border-[var(--border)]" data-testid="bp-rb-consistency">
          <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--muted)]">{t("bp.rb.consistency")}</div>
          {consistency.length === 0 ? (
            <div className="flex items-center gap-1.5 text-[11px] text-[#15803D]" data-testid="bp-rb-consistency-clean">
              <CheckCircle2 className="w-3.5 h-3.5" /> {t("bp.rb.consistency.clean")}
            </div>
          ) : (
            consistency.map((f, i) => (
              <Flag key={i} level={f.level || "loud"} testid={`bp-rb-consistency-${f.code}`}>
                {t(`bp.rb.consistency.${f.code}`, f.vars || {})}
              </Flag>
            ))
          )}
        </div>
      )}

      {/* 3.8 — visual audit (ruled 2026-08-08: one schema, one renderer) */}
      <VisualAuditPanel evidence={evidence} pagePaths={pagePaths} />

      {/* 4 — honesty-flag rail */}
      {rail?.length > 0 && (
        <div className="px-3 py-2 space-y-1 border-t border-[var(--border)]">
          <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--muted)]">{t("bp.rb.rail")}</div>
          {rail.map((f, i) => (
            <Flag key={i} level={f.level} testid={`bp-rb-rail-${f.code}`}>
              {t(`bp.rb.rail.${f.code}`, { text: f.text || "" })}
            </Flag>
          ))}
        </div>
      )}
    </div>
  );
}
